import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HotelSearchPageFilters from 'components/Pages/HotelSearchPage/HotelSearchPageFilters'
import OfferList from 'components/OfferList/OfferList'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import {
  OFFER_TYPE_BUNDLE_AND_SAVE,
} from 'constants/offer'
import { frontPageSortByTypes } from 'constants/offerListFilters'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import styled from 'styled-components'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import { LUXURY_ESCAPES } from 'constants/brands'
import { LUX_BUNDLE_LABEL } from 'constants/productLabel'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import { useAppDispatch } from 'hooks/reduxHooks'
import { replace } from 'connected-react-router'
import { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'

const LuxBundlesVertical = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const OffersSection = styled(VerticalSpacer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(32)};
`

const HeroContent = styled(LayoutContainer)`
  display: flex;
  height: 100%;
  width: 100%;
  color: ${props => props.theme.palette.neutral.default.eight};
  position: absolute;
  inset: 0;
  align-items: center;
  justify-content: center;
`

const LuxBundlesImageBanner = styled.div`
  height: ${rem(200)};
  position: relative;
  background-color: ${props => props.theme.palette.brand.primary.normal};

  ${mediaQueryUp.tablet} {
    height: ${rem(300)};
  }
`

interface Props {
  filters: App.OfferListFilters
  regionCode: string
}

function LuxBundlesPage(props: Props) {
  const { filters, regionCode } = props

  const dispatch = useAppDispatch()

  const headTitle = config.BRAND === LUXURY_ESCAPES ? 'Luxury Hotels & Resorts at the Best Prices on Earth' : 'Hotels & Resorts'
  const description = config.BRAND === LUXURY_ESCAPES ? 'Book exclusive special offers for luxury hotels and accommodation at insider prices. Best Price Guarantee.' : undefined

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const finalFilters = useMemo((): App.OfferListFilters => {
    return {
      ...filters,
      offerTypes: [OFFER_TYPE_BUNDLE_AND_SAVE],
      frontPageVisibleOnly: true,
      sortBy: filters.sortBy ?? 'homepage',
    }
  }, [filters])

  return <LuxBundlesVertical>
    <HeadData title={`${headTitle} - ${config.title} ${regionCode}`} description={description} />
    <ErrorBoundary fallback={null}>
      <PromptCardSelector tripReengagement />
    </ErrorBoundary>
    <LuxBundlesImageBanner>
      <HeroContent>
        <Group direction="vertical" horizontalAlign="center" gap={24}>
          <Heading variant="heading2" format="titlecase" as="h2" align="center" colour="highlight-primary">
            {LUX_BUNDLE_LABEL}s
          </Heading>
          <Heading variant="heading3" colour="neutral-eight" align="center">
            Some catchy subtitle would go here to explain this vertical
          </Heading>
        </Group>
      </HeroContent>
    </LuxBundlesImageBanner>
    <LayoutContainer relative>
      <OffersSection id="offers" gap={40}>
        <div>
          <HotelSearchPageFilters
            heading={
              <>Explore our bundles</>
            }
            filters={finalFilters}
            sortOptions={frontPageSortByTypes}
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
          />
        </div>
        <OfferList
          filters={finalFilters}
          interstitials={[{
            id: 'noResults',
            show: 'hasNoOffers',
            position: 'start',
            element: <VerticalSpacer gap={40}>
              <Heading variant="heading4">Unfortunately, there are no {LUX_BUNDLE_LABEL} hotels available at this time. </Heading>
              <BodyText variant="large">
                Check out our other hotels collection for more options or call our customer service team 24 hours, 7 days for personalised recommendations.
              </BodyText>
              <TextButton kind="primary" to="">
                View more hotels options
              </TextButton>
            </VerticalSpacer>,
          }]}
        />
      </OffersSection>
    </LayoutContainer>
  </LuxBundlesVertical>
}

function mapStateToProps(state: App.State) {
  return {
    filters: getUrlOfferListFilters(state),
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(LuxBundlesPage)
